import type { Order } from '@zenit/engine';

/** An API error: `status` is the HTTP status, `error` the API's code (`cash`, `free`, `asset`…). */
import type { PricesResponse } from '@zenit/contracts';
import { getLang } from './i18n';

type Role = 'participant' | 'admin';

export class ApiError extends Error { constructor(readonly status: number, readonly error: string) { super(`${status} ${error}`); } }

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch('/api' + path, {
    method, credentials: 'include', headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (r.status === 204) return undefined as T;
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, (j as { error?: string }).error ?? 'error');
  return j as T;
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  /** GET that answers null without a participant session (401; 403 for an admin or an unconfirmed account). */
  session: async <T>(): Promise<T | null> => { try { return await call<T>('GET', '/session'); } catch (e) { if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null; throw e; } },
  place: (body: Record<string, unknown>) => call<{ order: Order }>('POST', '/orders', body),
  edit: (id: string, body: Record<string, unknown>) => call<{ order: Order }>('PATCH', `/orders/${encodeURIComponent(id)}`, body),
  cancel: (id: string) => call<void>('POST', `/orders/${encodeURIComponent(id)}/cancel`),
  pillar: (sym: string, pillar: string) => call<void>('PUT', `/pillars/${encodeURIComponent(sym)}`, { pillar }),
  seen: () => call<void>('POST', '/session/seen'),
  signup: (b: { nombre: string; apellido: string; email: string; password: string }) => call<{ ok: true }>('POST', '/auth/signup', { ...b, lang: getLang() }), // the emails go in the app's language
  verify: (b: { token: string }) => call<{ ok: true; role: Role }>('POST', '/auth/verify', b),
  resend: (b: { email: string }) => call<{ ok: true }>('POST', '/auth/verify/resend', { ...b, lang: getLang() }),
  login: (b: { email: string; password: string }) => call<{ ok: true; role: Role }>('POST', '/auth/login', b),
  logout: () => call<void>('POST', '/auth/logout'),
  forgot: (b: { email: string }) => call<{ ok: true }>('POST', '/auth/password/forgot', { ...b, lang: getLang() }),
  reset: (b: { token: string; password: string }) => call<{ ok: true; role: Role }>('POST', '/auth/password/reset', b),
  prices: (syms: string[]) => call<PricesResponse>('POST', '/assets/prices', { syms }),
  adminSession: () => call<{ role: 'admin'; nombre: string; apellido: string; email: string }>('GET', '/admin/session'),
};

/** JSON without undefined/NaN fields, the way the prototype's engine received its order input. */
export const clean = (o: Record<string, unknown>) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && !(typeof v === 'number' && Number.isNaN(v))));

/**
 * Places or edits an order. The league is refetched whatever the answer: after a success the screens show the order;
 * after a refusal (422: cash, free units, an order that has just executed…) the form recomputes on fresh data and shows
 * the prototype's own message for the participant's current cash and units. `done` always runs, so the form never
 * stays busy.
 */
export async function submitOrder(call: () => Promise<{ order: Order }>, refresh: () => Promise<unknown>, done: (o: Order | null) => void): Promise<void> {
  let order: Order | null = null;
  try { order = (await call()).order; } catch { /* refused or unreachable: the refetch shows why */ }
  try { await refresh(); } catch { /* the form reopens on what it has */ }
  done(order);
}
